import { ClientCard } from '@/components/clients/client-card';
import { CreateClientModal } from '@/components/clients/create-client-modal';
import { useToastStore } from '@/store/toastStore';
import { EmptyState } from '@/components/ui/empty-state';
import { PageLayout } from '@/components/ui/page-layout';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StaticList } from '@/components/ui/static-list';
import { Button } from '@/components/ui/button';
import {
  useProviderClients,
  useRemoveProviderClient,
  useUpdateProviderClient,
} from '@/hooks/use-clients';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useTerms } from '@/hooks/use-terms';
import { Client } from '@/types/client';
import React, { useState, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { ThemedText } from '@/components/themed-text';
import { ErrorState } from '@/components/ui/error-state';
import { SkeletonListRow } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';
import { IconSymbol } from '@/components/ui/icon-symbol';
import { Avatar } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { LoadingButton } from '@/components/ui/loading-button';
import { getApiErrorMessage } from '@/utils/api-error';
import { brazilPhoneMask, isValidBrazilPhone } from '@/utils/brazil-phone';
import { formatWithMask } from 'react-native-mask-input';

export default function ProviderClientsScreen() {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { data: clients, isLoading, error } = useProviderClients(search);
  const updateClient = useUpdateProviderClient();
  const removeClient = useRemoveProviderClient();
  const addToast = useToastStore((s) => s.addToast);
  const terms = useTerms();

  const filteredClients = useMemo(() => {
    if (!clients) return [];
    // The search is already processed by the backend via the hook
    return clients;
  }, [clients]);

  const totalPages = Math.ceil((filteredClients.length || 0) / itemsPerPage);

  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredClients.slice(start, start + itemsPerPage);
  }, [filteredClients, currentPage, itemsPerPage]);

  const handleSearch = (query: string) => {
    setSearch(query);
    setCurrentPage(1);
  };

  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    phone: '',
    address: '',
  });

  const handleClientPress = (client: Client) => {
    setSelectedClient(client);
    setEditing(false);
    setEditForm({
      username: client.user.username || '',
      email: client.user.email || '',
      phone: formatWithMask({
        text: client.phone || '',
        mask: brazilPhoneMask(client.phone || ''),
      }).masked,
      address: client.address || '',
    });
  };

  // Depois do setup o nome é da conta do cliente; telefone/endereço seguem editáveis.
  const nameLocked = selectedClient?.setupCompleted === true;
  const closeModal = () => {
    setSelectedClient(null);
    setEditing(false);
  };

  const handleRemove = async () => {
    if (!selectedClient) return;
    try {
      await removeClient.mutateAsync(selectedClient.id);
      addToast(`${terms.agree('O', 'client')} ${terms.client} saiu da sua lista.`, 'success');
      setConfirmRemove(false);
      closeModal();
    } catch (err: unknown) {
      setConfirmRemove(false);
      addToast(getApiErrorMessage(err, `Erro ao excluir ${terms.client}.`), 'error');
    }
  };

  const handleUpdateSubmit = async () => {
    if (!selectedClient) return;
    if (editForm.phone.trim() && !isValidBrazilPhone(editForm.phone)) {
      addToast('Celular inválido. Use DDD + número.', 'error');
      return;
    }
    try {
      await updateClient.mutateAsync({
        clientId: selectedClient.id,
        data: {
          username: nameLocked ? undefined : editForm.username.trim() || undefined,
          email: nameLocked ? undefined : editForm.email.trim() || undefined,
          phone: editForm.phone.replace(/\D/g, '') || undefined,
          address: editForm.address.trim() || undefined,
        },
      });
      addToast(
        `Informações ${terms.agree('do', 'client')} ${terms.client} atualizadas com sucesso.`,
        'success',
      );
      closeModal();
    } catch (err: unknown) {
      addToast(getApiErrorMessage(err, `Erro ao atualizar ${terms.client}.`), 'error');
    }
  };

  const handleNewClient = () => setCreateModalOpen(true);

  return (
    <>
      <PageLayout
        title={terms.title.clientPlural}
        subtitle="Quem confia no seu trabalho"
        buttonTitle={`Adicionar ${terms.client}`}
        onButtonPress={handleNewClient}
        buttonTestID="client-list-new-client-btn"
      >
        <SearchInput
          value={search}
          onChangeText={setSearch}
          onSearch={handleSearch}
          placeholder={`Buscar ${terms.clientPlural} por nome ou email...`}
        />

        {isLoading ? (
          <View className="gap-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonListRow key={index} />
            ))}
          </View>
        ) : error ? (
          <ErrorState
            variant="inline"
            title={`Erro ao carregar ${terms.clientPlural}`}
            message={`Não foi possível carregar seus ${terms.clientPlural}. Tente novamente.`}
          />
        ) : filteredClients.length === 0 ? (
          <EmptyState
            icon="people-outline"
            title={
              search
                ? `${terms.agree('Nenhum', 'client')} ${terms.client} ${terms.agree('encontrado', 'client')}`
                : `${terms.agree('Nenhum', 'client')} ${terms.client} ${terms.agree('cadastrado', 'client')} ainda`
            }
            description={
              search
                ? `Tente ajustar a busca ou cadastre ${terms.agree('um', 'client')} ${terms.agree('novo', 'client')} ${terms.client}.`
                : `Cadastre ${terms.clientPlural} para vincular a ${terms.enrollmentPlural} e cobranças.`
            }
            actionLabel={`Cadastrar ${terms.client}`}
            onAction={handleNewClient}
            className="flex-1 py-12"
          />
        ) : (
          <>
            <StaticList
              data={paginatedClients}
              keyExtractor={(item) => item.id}
              renderItem={(item) => (
                <View key={item.id} className="mb-3">
                  <ClientCard client={item} onPress={() => handleClientPress(item)} />
                </View>
              )}
            />

            {filteredClients.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages || 1}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                itemsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </PageLayout>

      <CreateClientModal open={createModalOpen} onOpenChange={setCreateModalOpen} />

      {selectedClient && (
        <Dialog open={!!selectedClient} onOpenChange={(open) => !open && closeModal()}>
          <DialogContent className={editing ? 'max-w-md' : ''}>
            <DialogClose onPress={closeModal} />

            {editing ? (
              <View className="shrink">
                <DialogHeader className="mb-4">
                  <DialogTitle>
                    {`Atualizar informações ${terms.agree('do', 'client')} ${terms.client}`}
                  </DialogTitle>
                  <DialogDescription className="mt-1">
                    {nameLocked
                      ? `Nome e email são da conta ${terms.agree('do', 'client')} ${terms.client}; telefone e endereço você pode ajustar.`
                      : 'Ajuste os dados cadastrais.'}
                  </DialogDescription>
                </DialogHeader>

                <ScrollView className="max-h-80 shrink" keyboardShouldPersistTaps="handled">
                  <View className="gap-4">
                    <View>
                      <ThemedText className="mb-1.5 text-sm text-muted-foreground">Nome</ThemedText>
                      <Input
                        value={editForm.username}
                        onChangeText={(t) => setEditForm((f) => ({ ...f, username: t }))}
                        placeholder={`Nome ${terms.agree('do', 'client')} ${terms.client}`}
                        autoCapitalize="words"
                        editable={!nameLocked}
                      />
                    </View>
                    <View>
                      <ThemedText className="mb-1.5 text-sm text-muted-foreground">
                        Email
                      </ThemedText>
                      <Input
                        value={editForm.email}
                        onChangeText={(t) => setEditForm((f) => ({ ...f, email: t }))}
                        placeholder="email@exemplo.com"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoCorrect={false}
                        editable={!nameLocked}
                      />
                    </View>
                    <View>
                      <ThemedText className="mb-1.5 text-sm text-muted-foreground">
                        Telefone
                      </ThemedText>
                      <Input
                        value={editForm.phone}
                        onChangeText={(t) =>
                          setEditForm((f) => ({
                            ...f,
                            phone: formatWithMask({ text: t, mask: brazilPhoneMask(t) }).masked,
                          }))
                        }
                        placeholder="(00) 00000-0000"
                        keyboardType="phone-pad"
                      />
                    </View>
                    <View>
                      <ThemedText className="mb-1.5 text-sm text-muted-foreground">
                        Endereço
                      </ThemedText>
                      <Input
                        value={editForm.address}
                        onChangeText={(t) => setEditForm((f) => ({ ...f, address: t }))}
                        placeholder="Endereço completo"
                      />
                    </View>
                  </View>
                </ScrollView>

                <DialogFooter className="mt-6 flex-row justify-end gap-2">
                  <Button variant="outline" title="Cancelar" onPress={() => setEditing(false)} />
                  <LoadingButton
                    title="Salvar"
                    onPress={handleUpdateSubmit}
                    loading={updateClient.isPending}
                  />
                </DialogFooter>
              </View>
            ) : (
              <>
                <DialogHeader className="mb-6 items-center">
                  <Avatar
                    src={selectedClient.user.image}
                    fallback={selectedClient.user.username?.substring(0, 2).toUpperCase()}
                    className="mb-3 h-20 w-20"
                  />
                  <DialogTitle className="text-center">{selectedClient.user.username}</DialogTitle>
                  <DialogDescription className="text-center">
                    {selectedClient.user.email}
                  </DialogDescription>
                </DialogHeader>

                <View className="gap-4">
                  <DetailRow
                    icon="phone"
                    label="Telefone"
                    value={selectedClient.phone || 'Não informado'}
                  />
                  <DetailRow
                    icon="location.fill"
                    label="Endereço"
                    value={selectedClient.address || 'Não informado'}
                  />
                  <View className="my-2 h-[1px] bg-border" />
                  <DetailRow
                    icon="doc.text.fill"
                    label={terms.title.enrollmentPlural}
                    value={`${selectedClient.enrollments?.length || 0} ${
                      terms.enrollmentPlural
                    } ${terms.agree('ativos', 'enrollment')}`}
                  />
                </View>

                <DialogFooter className="mt-6 flex-row justify-end gap-2">
                  <Button
                    variant="outline"
                    title="Excluir"
                    onPress={() => setConfirmRemove(true)}
                    testID="client-detail-remove-btn"
                  />
                  <Button
                    title="Editar"
                    onPress={() => setEditing(true)}
                    testID="client-detail-edit-btn"
                  />
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={`Excluir ${terms.client}?`}
        message={'Sai da sua lista. Cobranças e atendimentos antigos continuam no histórico.'}
        confirmLabel="Excluir"
        destructive
        onConfirm={handleRemove}
      />
    </>
  );
}

function DetailRow({ icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <View className="flex-row items-center gap-3">
      <View className="h-10 w-10 items-center justify-center rounded-full bg-muted/20">
        <IconSymbol name={icon} size={20} color="#666" />
      </View>
      <View className="flex-1">
        <ThemedText className="text-xs text-muted-foreground">{label}</ThemedText>
        <ThemedText type="defaultSemiBold">{value}</ThemedText>
      </View>
    </View>
  );
}
